import { defineStore, storeToRefs } from "pinia";
import api from "@/services/api";
import { useAuthStore, useTenantsStore, useSitesStore } from '@/stores';
import type IUser from "@/models/responses/IUser";
import IUserRoleResponse from "@/models/responses/IUserRoleResponse";
import UserRoleResponse from "@/models/frontend/UserRoleResponse";
import User from "@/models/frontend/User";

export const useUsersStore = defineStore("users", {
  state: () => ({
    users: [] as User[],
    userRoles: [] as UserRoleResponse[],
    error: "" as any,
    loaded: false,
  }),
  actions: {
    async fetchAll(force = false) {
      const authStore = useAuthStore();

      const { isAuthenticated } = storeToRefs(authStore);

      if (!isAuthenticated.value) return;
      if (!force && this.loaded) return;

      this.users = [];
      await api
        .get<IUser[]>("v1.0/users")
        .then((response) => {
          this.users = response.data.map(this.mapToFrontend);
          this.loaded = true;
        })
        .catch((error) => (this.error = error));
    },
    async getRoles(id: number) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      this.userRoles = [];
      await api
        .get<IUserRoleResponse[]>(`v1.0/users/${id}/roles`)
        .then((response) => (this.userRoles = response.data.map(this.mapUserRoleResponseToFrontend)))
        .catch((error) => (this.error = error));
    },
    async deleteUser(id: number) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      await api
        .delete(`v1.0/users/${id}`)
        .then(() => {
          this.users = this.users.filter((user) => user.id !== id);
        })
        .catch((error) => (this.error = error));
    },
    mapToFrontend(user: IUser) {
      return new User(
        user.id,
        user.email,
        user.name,
        user.isSystem);
    },
    mapUserRoleResponseToFrontend(userRoleResponse: IUserRoleResponse) {
      const tenantsStore = useTenantsStore();
      const sitesStore = useSitesStore();
      return new UserRoleResponse(
        userRoleResponse.code,
        userRoleResponse.displayName,
        userRoleResponse.tenantId ? tenantsStore.getById(userRoleResponse.tenantId) : undefined,
        userRoleResponse.siteId ? sitesStore.getById(userRoleResponse.siteId) : undefined);
    }
  },
  getters: {
    getUserById: (state) => (id: number): User => {
      return <User>(state.users as User[]).find((user) => user.id === id);
    },
  },
});
